/**
 * Cutting a vector tile down to the features anchored inside some polygons. A crown archive's
 * features carry their anchor — `ax`, `ay`, WGS84: a crown's representative point, an MLS stem's
 * position, for its crown too — the very point the growing stock assigns a tree to a unit by, so
 * the map keeps exactly the trees the figures count. A point without one is its own anchor. Geometry, ids (0 included), properties
 * and extent are written back as they were read: no re-quantisation.
 */
import { VectorTile, type VectorTileFeature } from '@mapbox/vector-tile';
import { fromVectorTileJs, type VectorTileLike } from '@maplibre/vt-pbf';
import { PbfReader } from 'pbf';

export const ANCHOR = { lon: 'ax', lat: 'ay' } as const;

export interface Filtered {
  bytes: Uint8Array;
  /** Features that carry no anchor (an archive made before anchors): kept, as the coarse mask would. */
  unanchored: number;
}

/**
 * A feature's anchor: `ax`, `ay` when it carries them; else, for a point, the point itself
 * (photo crowns' points carry no anchor — the point is it, quantised to the tile); else none.
 */
function anchorOf(feature: VectorTileFeature, z: number, x: number, y: number): [number, number] | null {
  const lon = feature.properties[ANCHOR.lon];
  const lat = feature.properties[ANCHOR.lat];
  if (typeof lon === 'number' && typeof lat === 'number') return [lon, lat];
  if (feature.type !== 1) return null;
  const p = feature.loadGeometry()[0]?.[0];
  if (!p) return null;
  const n = 2 ** z;
  const tx = x + p.x / feature.extent;
  const ty = y + p.y / feature.extent;
  return [(tx / n) * 360 - 180, (Math.atan(Math.sinh(Math.PI * (1 - (2 * ty) / n))) * 180) / Math.PI];
}

/** The tile at z/x/y cut to the features anchored inside. */
export function filterTile(bytes: Uint8Array, inside: (lon: number, lat: number) => boolean, z: number, x: number, y: number): Filtered {
  const tile = new VectorTile(new PbfReader(bytes));
  let unanchored = 0;
  const layers: VectorTileLike['layers'] = {};
  for (const [name, layer] of Object.entries(tile.layers)) {
    const kept: VectorTileFeature[] = [];
    for (let i = 0; i < layer.length; i++) {
      const feature = layer.feature(i);
      const at = anchorOf(feature, z, x, y);
      if (!at) {
        unanchored++;
        kept.push(feature);
      } else if (inside(at[0], at[1])) kept.push(feature);
    }
    layers[name] = { version: layer.version, name, extent: layer.extent, length: kept.length, feature: (i: number) => kept[i]! } as VectorTileLike['layers'][string];
  }
  return { bytes: fromVectorTileJs({ layers }), unanchored };
}
